import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import './search.css'

/** Uses the existing modal focus/scroll pattern, with native dialog inertness and dynamic focus trapping. */
export default function SearchShell({
  children,
  onClose,
}: {
  children: ReactNode
  onClose: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const [fallback] = useState(
    () =>
      typeof HTMLDialogElement === 'undefined' ||
      typeof HTMLDialogElement.prototype.showModal !== 'function' ||
      !('inert' in HTMLElement.prototype)
  )
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  }, [onClose])
  useEffect(() => {
    const dialog = dialogRef.current!
    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    const supportsInert = 'inert' in HTMLElement.prototype
    const background = new Map<
      HTMLElement,
      {
        inert: boolean
        ownInert: PropertyDescriptor | undefined
        ariaHidden: string | null
        pointerEvents: string
        pointerPriority: string
      }
    >()
    const disableBackground = () => {
      for (const element of Array.from(document.body.children) as HTMLElement[]) {
        if (element === dialog || element === backdropRef.current || background.has(element))
          continue
        background.set(element, {
          inert: element.inert,
          ownInert: Object.getOwnPropertyDescriptor(element, 'inert'),
          ariaHidden: element.getAttribute('aria-hidden'),
          pointerEvents: element.style.getPropertyValue('pointer-events'),
          pointerPriority: element.style.getPropertyPriority('pointer-events'),
        })
        element.inert = true
        if (fallback) {
          element.setAttribute('aria-hidden', 'true')
          element.style.setProperty('pointer-events', 'none', 'important')
        }
      }
    }
    disableBackground()
    const backgroundObserver = new MutationObserver(disableBackground)
    backgroundObserver.observe(document.body, { childList: true })
    document.body.style.overflow = 'hidden'
    if (!fallback) dialog.showModal()
    else dialog.setAttribute('open', '')
    const focusInput = () =>
      (dialog.querySelector('input') ?? dialog.querySelector('button'))?.focus()
    focusInput()
    const confineFocus = (event: FocusEvent) => {
      if (event.target instanceof Node && !dialog.contains(event.target)) {
        event.stopImmediatePropagation()
        focusInput()
      }
    }
    const blockBackgroundPointer = (event: Event) => {
      if (
        !(event.target instanceof Node) ||
        dialog.contains(event.target) ||
        backdropRef.current?.contains(event.target)
      )
        return
      event.preventDefault()
      event.stopImmediatePropagation()
    }
    const focusObserver = new MutationObserver(() => {
      if (!dialog.contains(document.activeElement)) focusInput()
    })
    focusObserver.observe(dialog, { childList: true, subtree: true })
    const trap = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.isComposing) {
        event.preventDefault()
        event.stopImmediatePropagation()
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input, a[href], [tabindex="0"]'
        )
      )
        .filter((el) => !el.hidden)
        .sort((first, second) =>
          first.compareDocumentPosition(second) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        )
      const first = items[0],
        last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    const nativeBack = (event: Event) => {
      event.preventDefault()
      closeRef.current()
    }
    document.addEventListener('keydown', trap, true)
    document.addEventListener('focusin', confineFocus, true)
    if (fallback) {
      document.addEventListener('pointerdown', blockBackgroundPointer, true)
      document.addEventListener('click', blockBackgroundPointer, true)
      document.addEventListener('touchstart', blockBackgroundPointer, {
        capture: true,
        passive: false,
      })
    }
    window.addEventListener('optioneer:native-back', nativeBack)
    const resize = () => {
      const viewport = window.visualViewport
      dialog.style.setProperty(
        '--search-viewport-height',
        `${viewport?.height ?? window.innerHeight}px`
      )
      dialog.style.setProperty('--search-viewport-top', `${viewport?.offsetTop ?? 0}px`)
    }
    resize()
    window.visualViewport?.addEventListener('resize', resize)
    window.visualViewport?.addEventListener('scroll', resize)
    window.addEventListener('resize', resize)
    return () => {
      focusObserver.disconnect()
      backgroundObserver.disconnect()
      document.removeEventListener('keydown', trap, true)
      document.removeEventListener('focusin', confineFocus, true)
      document.removeEventListener('pointerdown', blockBackgroundPointer, true)
      document.removeEventListener('click', blockBackgroundPointer, true)
      document.removeEventListener('touchstart', blockBackgroundPointer, true)
      window.removeEventListener('optioneer:native-back', nativeBack)
      window.visualViewport?.removeEventListener('resize', resize)
      window.visualViewport?.removeEventListener('scroll', resize)
      window.removeEventListener('resize', resize)
      for (const [element, previous] of background) {
        if (supportsInert) element.inert = previous.inert
        else if (previous.ownInert) Object.defineProperty(element, 'inert', previous.ownInert)
        else Reflect.deleteProperty(element, 'inert')
        if (fallback) {
          if (previous.ariaHidden === null) element.removeAttribute('aria-hidden')
          else element.setAttribute('aria-hidden', previous.ariaHidden)
          if (previous.pointerEvents)
            element.style.setProperty(
              'pointer-events',
              previous.pointerEvents,
              previous.pointerPriority
            )
          else element.style.removeProperty('pointer-events')
        }
      }
      document.body.style.overflow = previousOverflow
      if (!fallback && dialog.open) dialog.close()
      else dialog.removeAttribute('open')
      if (previousFocus?.isConnected) previousFocus.focus()
      if (!previousFocus?.isConnected || document.activeElement !== previousFocus) {
        Array.from(
          document.querySelectorAll<HTMLButtonElement>('button[aria-label="Search Optioneer"]')
        )
          .find((trigger) => !trigger.disabled && trigger.getClientRects().length > 0)
          ?.focus()
      }
    }
  }, [fallback])
  return createPortal(
    <>
      {fallback && (
        <div
          ref={backdropRef}
          className="optioneer-search-backdrop sentry-block"
          aria-hidden="true"
          data-search-backdrop
          onClick={() => closeRef.current()}
        />
      )}
      <dialog
        ref={dialogRef}
        className={`optioneer-search sentry-block${fallback ? ' optioneer-search--fallback' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="optioneer-search-title"
        data-private-search
        onCancel={(event) => {
          event.preventDefault()
          onClose()
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) onClose()
        }}
      >
        <div className="search-surface flex min-h-0 flex-col bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100">
          <div className="flex shrink-0 items-center justify-between gap-2 px-4 py-2">
            <h2 id="optioneer-search-title" className="text-lg font-semibold">
              Search Optioneer
            </h2>
            <button
              type="button"
              aria-label="Close search"
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 dark:hover:bg-slate-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {children}
        </div>
      </dialog>
    </>,
    document.body
  )
}
